import { Component, OnInit, AfterViewInit } from '@angular/core';
import { FunnelService } from '../services/funnel.service';
import { Router } from '@angular/router';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ConsentModalComponent } from "../consent-modal/consent-modal.component";
import { HealthMetricsRequest, QuestionAnswerItem, ReferralCreateRequest, ReferralCreateResponse, SubmitAnswersRequest } from '../models/models';
// Removed ngx-intl-tel-input usage

@Component({
  selector: 'app-funnel',
  templateUrl: './funnel.component.html',
  styleUrls: ['./funnel.component.scss']
})
export class FunnelComponent implements OnInit, AfterViewInit {

  readonly options: any = {
    types: ["geocode"],
    componentRestrictions: { country: "us" },
  };

  // PrimeNG Steps properties
  currentStepIndex: number = 0;
  stepItems: any[] = [];

  // State restoration
  private restoredStepIndex: number | null = null;

  heightFeet: number | null = null;
  heightInches: number | null = null;
  weight: number | null = null;
  progressValue: number = 0;

  // Final Step Fields
  fullName: string = '';  // For Name Input
  email: string = '';     // For Email Input
  phone: string = '';     // For Phone Input
  displayPhone: string = ''; // For showing in verification UI
  dob: string = '';     // For dob Input
  goalWeight: number | null = null;     // For goalWeight Input
  city: string = '';
  state: string = '';
  zip: string = '';
  address: string = '';

  // Internal validation state
  dobError: string = '';
  dobAsDateInput: string = '';
  isMobile: boolean = false;

  // OTP/Verification state
  showVerification: boolean = false;
  otpValue: string = '';
  verificationError: string = '';
  existingUserError: string = '';
  referralId: string | null = null;
  resendSeconds: number = 0;
  verifying: boolean = false;
  private resendTimerId: any = null;

  otpConfig = {
    length: 6,
    allowNumbersOnly: true,
    disableAutoFocus: false,
    inputClass: 'otp-input text-center mb-2',
    placeholder: ''
  } as any;

  questions: any[] = [];

  // Consent checkbox state
  consentChecked: boolean = false;
  // ngx-intl-tel-input helpers
  // Removed intl-tel helpers

  ngOnInit(): void {
    this.funnelService.getQuestions('wellness').subscribe({
      next: (res) => {
        this.questions = (res || [])
          .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
          .map((q: any) => ({
            ...q,
            answer: q.type === 'checkbox' ? [] : ''
          }));

        // Initialize step items for PrimeNG Steps
        this.initializeStepItems();

        // Restore saved state after questions are loaded
        const savedState = this.readState();
        if (savedState) {
          this.restoreStateToQuestions(savedState);
        }

        // Set device detection and sync date input value
        this.isMobile = this.detectMobile();
        this.dobAsDateInput = this.toDateInputValue(this.dob);
      },
      error: (err) => {
        console.error('Failed to load questions', err);
        this.questions = [];
      }
    });
  }

  ngAfterViewInit(): void {
    // Restore step index after view initialization
    if (this.restoredStepIndex !== null) {
      // Clamp to available steps
      const maxStep = this.questions.length;
      this.currentStepIndex = Math.min(Math.max(0, this.restoredStepIndex), maxStep);
      this.restoredStepIndex = null;

      // Persist the restored state to ensure it's saved
      this.persistState();
    }
  }

  initializeStepItems(): void {
    this.stepItems = this.questions.map((question, index) => ({
      label: `Step ${index + 1}`,
      command: (event: any) => {
        this.currentStepIndex = index;
      }
    }));

    // Add submit step
    this.stepItems.push({
      label: 'Submit',
      command: (event: any) => {
        this.currentStepIndex = this.questions.length;
      }
    });
  }

  constructor(private funnelService: FunnelService,
              private router: Router,
              private _modalService: BsModalService
  )
  {}

  // localStorage persistence helpers
  private readState(): any {
    try {
      const saved = localStorage.getItem('funnelStateV1');
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.warn('Failed to read funnel state from localStorage:', error);
      return null;
    }
  }

  private persistState(): void {
    try {
      const state = {
        stepIndex: this.currentStepIndex,
        heightFeet: this.heightFeet,
        heightInches: this.heightInches,
        weight: this.weight,
        dob: this.dob,
        goalWeight: this.goalWeight,
        fullName: this.fullName,
        email: this.email,
        phone: this.phone,
        address: this.address,
        city: this.city,
        state: this.state,
        zip: this.zip,
        consentChecked: this.consentChecked,
        questionAnswers: this.questions.map(q => ({
          id: q.id ?? q.index,
          answer: q.answer
        }))
      };
      localStorage.setItem('funnelStateV1', JSON.stringify(state));
    } catch (error) {
      console.warn('Failed to persist funnel state to localStorage:', error);
    }
  }

  private clearState(): void {
    try {
      localStorage.removeItem('funnelStateV1');
      localStorage.removeItem('referralId');
    } catch (error) {
      console.warn('Failed to clear funnel state from localStorage:', error);
    }
  }

  private restoreStateToQuestions(savedState: any): void {
    if (!savedState) return;

    // Restore scalar fields
    this.heightFeet = savedState.heightFeet ?? null;
    this.heightInches = savedState.heightInches ?? null;
    this.weight = savedState.weight ?? null;
    this.dob = savedState.dob ?? '';
    this.goalWeight = savedState.goalWeight ?? null;
    this.fullName = savedState.fullName ?? '';
    this.email = savedState.email ?? '';
    this.phone = savedState.phone ?? '';
    this.address = savedState.address ?? '';
    this.city = savedState.city ?? '';
    this.state = savedState.state ?? '';
    this.zip = savedState.zip ?? '';
    this.consentChecked = savedState.consentChecked ?? false;

    // Restore question answers
    if (savedState.questionAnswers && Array.isArray(savedState.questionAnswers)) {
      savedState.questionAnswers.forEach((savedAnswer: any) => {
        const question = this.questions.find(q => (q.id ?? q.index) === savedAnswer.id);
        if (question && savedAnswer.answer !== undefined) {
          question.answer = savedAnswer.answer;
        }
      });
    }

    // Store step index for restoration after view init
    this.restoredStepIndex = savedState.stepIndex ?? 0;

    // Recalculate BMI if height/weight are restored (without persisting)
    if (this.heightFeet !== null || this.heightInches !== null || this.weight !== null) {
      this.calculateBMISilent();
    }

    this.currentStepIndex = this.restoredStepIndex;

  }

  // Navigation methods for PrimeNG Steps
  nextStep(): void {
    if (this.currentStepIndex < this.questions.length) {
      this.currentStepIndex++;
      this.persistState();
    }
  }

  previousStep(): void {
    if (this.currentStepIndex > 0) {
      this.currentStepIndex--;
      this.persistState();
    }
  }

  // Handle final step field changes
  onFinalStepFieldChange(): void {
    this.persistState();
  }

  // Handle goal weight changes
  onGoalWeightChange(): void {
    this.persistState();
  }

  // Helper method for template to check if answer includes option
  isAnswerChecked(question: any, option: string): boolean {
    return Array.isArray(question.answer) && question.answer.includes(option);
  }

  onOtpChange(code: string | null): void {
    this.otpValue = (code || '').toString();
  }

  // Phone handled via masked input + isPhoneValid()

  // Function to calculate BMI when height or weight changes
  calculateBMI(): void {
    if (this.isHeightFeetValid() && this.isHeightInchesValid() && this.isWeightValid() && this.heightFeet !== null && this.heightInches !== null && this.weight !== null) {
      const totalHeightInches = (this.heightFeet * 12) + this.heightInches;
      const heightSquared = totalHeightInches * totalHeightInches;
      const bmi = (this.weight / heightSquared) * 703;
      this.progressValue = bmi;
    } else {
      this.progressValue = 0;
    }

    // Persist state after BMI calculation
    this.persistState();
  }

  // Silent BMI calculation for restoration (no persistence)
  private calculateBMISilent(): void {
    if (this.isHeightFeetValid() && this.isHeightInchesValid() && this.isWeightValid() && this.heightFeet !== null && this.heightInches !== null && this.weight !== null) {
      const totalHeightInches = (this.heightFeet * 12) + this.heightInches;
      const heightSquared = totalHeightInches * totalHeightInches;
      const bmi = (this.weight / heightSquared) * 703;
      this.progressValue = bmi;
    } else {
      this.progressValue = 0;
    }
  }

  // DOB formatting and validation
  onDobInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let raw = input.value || '';
    // Only allow digits and dashes
    raw = raw.replace(/[^0-9-]/g, '');

    // Enforce max length for MM-DD-YYYY (10 chars)
    if (raw.length > 10) {
      raw = raw.slice(0, 10);
      input.value = raw;
    }

    // Extract digits only for formatting
    const digitsOnly = raw.replace(/\D/g, '').slice(0, 8);

    // Prevent entering more than 4 digits for year
    if (digitsOnly.length > 4) {
      const yearPart = digitsOnly.substring(4, 8);
      if (yearPart.length >= 4 && raw.length > 10) {
        raw = raw.slice(0, 10);
        input.value = raw;
      }
    }

    if (raw && /[^0-9-]/.test(raw)) {
      this.dobError = 'Invalid date format.';
    } else {
      this.dobError = '';
    }

    const parts: string[] = [];
    if (digitsOnly.length > 0) {
      parts.push(digitsOnly.substring(0, Math.min(2, digitsOnly.length)));
    }
    if (digitsOnly.length > 2) {
      parts.push(digitsOnly.substring(2, Math.min(4, digitsOnly.length)));
    }
    if (digitsOnly.length > 4) {
      parts.push(digitsOnly.substring(4, Math.min(8, digitsOnly.length)));
    }
    this.dob = parts.join('-');
    this.dobAsDateInput = this.toDateInputValue(this.dob);
    this.validateDob(false);

    // Persist state after DOB change
    this.persistState();
  }

  onDobDateChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value || '';
    // Expect value in format YYYY-MM-DD; convert to MM-DD-YYYY
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (match) {
      const y = match[1];
      const m = match[2];
      const d = match[3];
      this.dob = `${m}-${d}-${y}`;
      this.dobAsDateInput = value;
      this.validateDob(false);
      this.persistState();
    }
  }

  validateDob(showIncompleteError: boolean = true): boolean {
    const dobStr = this.dob;

    if (this.dobError === 'Invalid date format.' && (!/^[0-9-]*$/.test(dobStr) || dobStr.length < 10)) {
      return false;
    }

    this.dobError = '';
    const match = /^(\d{2})-(\d{2})-(\d{4})$/.exec(dobStr);
    if (!match) {
      if (showIncompleteError && dobStr.length > 0) {
        this.dobError = 'Invalid date format.';
      }
      return false;
    }
    const month = Number(match[1]);
    const day = Number(match[2]);
    const year = Number(match[3]);

    if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900) {
      this.dobError = 'Invalid date format.';
      return false;
    }

    const date = new Date(year, month - 1, day);
    if (isNaN(date.getTime()) || date.getMonth() + 1 !== month || date.getDate() !== day || date.getFullYear() !== year) {
      this.dobError = 'Invalid date format.';
      return false;
    }

    const today = new Date();
    let age = today.getFullYear() - year;
    const m = today.getMonth() - (month - 1);
    if (m < 0 || (m === 0 && today.getDate() < day)) {
      age--;
    }

    if (age > 75) {
      this.dobError = 'Max age is 75';
      return false;
    }
    if (age < 18) {
      this.dobError = 'Min age is 18';
      return false;
    }

    this.dobError = '';
    return true;
  }

  get isDobValid(): boolean {
    return this.validateDob(false);
  }

  // BMI validations
  isHeightFeetValid(): boolean {
    return typeof this.heightFeet === 'number' && this.heightFeet >= 4.2 && this.heightFeet <= 8.4;
  }

  isHeightInchesValid(): boolean {
    return typeof this.heightInches === 'number' && this.heightInches >= 0 && this.heightInches <= 11;
  }

  isWeightValid(): boolean {
    return typeof this.weight === 'number' && this.weight >= 50 && this.weight <= 500;
  }

  isBmiSectionValid(): boolean {
    return this.isHeightFeetValid() && this.isHeightInchesValid() && this.isWeightValid();
  }

  // Goal weight validation
  goalWeightError(): string {
    if (this.goalWeight === null || this.goalWeight <= 0) {
      return '';
    }
    if (this.goalWeight < 90) {
      return 'Min recommended weight is 90 lbs';
    }
    if (this.weight && this.goalWeight >= this.weight) {
      return 'Goal weight must be less than current weight';
    }
    return '';
  }

  isGoalWeightValid(): boolean {
    const err = this.goalWeightError();
    return !err && this.goalWeight !== null;
  }

  // Email & Phone validation
  isEmailValid(): boolean {
    if (!this.email) return false;
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(this.email);
  }

  isPhoneValid(): boolean {
    if (!this.phone) return false;
    const normalized = this.phone.replace(/[^0-9+]/g, '');
    const re = /^\+?[0-9]{10,15}$/;
    return re.test(normalized);
  }

  canSubmit(): boolean {
    return this.fullName.trim().length > 0 && this.isEmailValid() && this.isPhoneValid() &&
      this.address.trim().length > 0 &&
      this.city.trim().length > 0 &&
      this.state.trim().length > 0 &&
      this.zip.trim().length > 0 &&
      this.consentChecked;
  }

  answers: string[] = [];

  // Track whether a question is answered
  answeredQuestions: Set<number> = new Set();

  // Track user answers
  onAnswerChange(questionId: number, type: string, qIndex: number , event: Event) {
    const target = event.target as HTMLInputElement;
    const question = this.questions.find(q => q.id === questionId || q.index === questionId);
    if (!question) return;

    if (type === 'checkbox') {
      if (!Array.isArray(question.answer)) {
        question.answer = [];
      }
      const optionValue = question.options[qIndex];

      if (target.checked) {
        // Only add if not already present
        if (!question.answer.includes(optionValue)) {
          question.answer.push(optionValue);
        }
      } else {
        // Remove by option value, not by index
        question.answer = question.answer.filter((answer: any) => answer !== optionValue);
      }
    } else if (type === 'radio') {
      question.answer = question.options[qIndex];
    }

    // Persist state after answer change
    this.persistState();
  }

  // Check if a question is answered
  isAnswered(questionId: number): boolean {
    const question = this.questions.find(q => q.id === questionId || q.index === questionId);
    if (!question) return false;

    if (question.type === 'checkbox') {
      return Array.isArray(question.answer) && question.answer.length > 0;
    }
    if (question.type === 'radio') {
      return !!question.answer;
    }
    if (question.type === 'dob') {
      return this.isDobValid;
    }
    if (question.type === 'bmi') {
      return this.isBmiSectionValid();
    }
    if (question.type === 'weight') {
      return this.isGoalWeightValid();
    }
    return false;
  }

  // Verification helpers
  private startResendTimer(): void {
    this.resendSeconds = 60;
    if (this.resendTimerId) {
      clearInterval(this.resendTimerId);
    }
    this.resendTimerId = setInterval(() => {
      if (this.resendSeconds > 0) {
        this.resendSeconds--;
      } else {
        clearInterval(this.resendTimerId);
        this.resendTimerId = null;
      }
    }, 1000);
  }

  private startVerificationFlow(): void {
    this.showVerification = true;
    this.otpValue = '';
    this.verificationError = '';
    this.funnelService.sendVerificationCode(this.email, 'phone').subscribe({
      next: (res) => {
        // Use server-returned phone, fallback to local phone
        this.displayPhone = res?.phone || this.phone || '';
        this.startResendTimer();
      },
      error: (err) => {
        console.error('Failed to send verification code', err);
        this.verificationError = 'Failed to send code. Please try again.';
      }
    });
  }

  handleVerificationBack(): void {
    this.showVerification = false;
    setTimeout(() => {
      this.currentStepIndex = this.questions.length; // last step (submit step)
    });
  }

  resendCode(): void {
    if (this.resendSeconds > 0) return;
    this.funnelService.sendVerificationCode(this.email, 'phone').subscribe({
      next: (res) => {
        this.displayPhone = res?.phone || this.displayPhone || this.phone || '';
        this.startResendTimer();
      },
      error: () => this.verificationError = 'Failed to resend code. Please try again.'
    });
  }

  onPhoneChangedAfterReferral(): void {
    const storedId = (() => { try { return localStorage.getItem('referralId'); } catch { return null; } })();
    if (!storedId) return;
    const newPhone = this.toE164Us(this.phone);
    if (!newPhone) return;
    this.funnelService.updateReferral(storedId, { phone: newPhone }).subscribe({
      next: () => {},
      error: (err) => console.error('Failed to update referral phone', err)
    });
  }

  verifyOtp(): void {
    this.verificationError = '';
    const code = (this.otpValue || '').trim();
    if (!code || code.length < 6) {
      this.verificationError = 'Please enter the 6-digit code.';
      return;
    }
    this.verifying = true;
    this.funnelService.verifyCode(this.email, code).subscribe({
      next: () => {
        this.verifying = false;
        this.showVerification = false;
        // Clear state before navigating to thank-you
        this.clearState();
        this.router.navigate(['/thank-you']);
      },
      error: (err) => {
        this.verifying = false;
        console.error('Verification failed', err);
        this.verificationError = 'Invalid or expired code. Please try again.';
      }
    });
  }

  submitAnswers(){

    this.existingUserError = '';
    const referralPayload: ReferralCreateRequest = {
      fullName: this.fullName,
      email: this.email,
      phone: this.toE164Us(this.phone),
      address: this.address,
      appNo: '',
      city: this.city,
      state: (this.state || '').toUpperCase(),
      zip: this.zip,
      dob: this.toIsoDate(this.dob),
      birthSex: this.extractBirthSex() || '',
      associatedType: 'Wellness',
      organizationSlug: 'wellness',
    };

    this.funnelService.createReferral(referralPayload).subscribe({
      next: (refRes) => {
        const referralId = refRes?.referral?.id;
        const isExisting = !!refRes?.isExisting;
        const isPhoneVerified = !!refRes?.referral?.isPhoneVerified;

        if (isExisting && !isPhoneVerified) {
          this.referralId = referralId ?? null;
          this.startVerificationFlow();
          return;
        }
        if (isExisting && isPhoneVerified) {
          this.existingUserError = 'You have already submitted the form.';
          return;
        }

        if (referralId) {
          this.referralId = referralId;
          try { localStorage.setItem('referralId', referralId); } catch {}

          // Build filtered answers (exclude DOB, BirthSex, BMI, IdealWeight)
          const answers: QuestionAnswerItem[] = this.questions
            .filter((q: any) => !this.isExcludedFromAnswers(q))
            .map((q: any) => {
              let value: string = '';
              if (q.type === 'checkbox') {
                value = Array.isArray(q.answer) ? q.answer.join(', ') : '';
              } else {
                value = typeof q.answer === 'string' ? q.answer : '';
              }
              return {
                questionId: String(q.id ?? q.index),
                answer: Boolean(value && (Array.isArray(q.answer) ? q.answer.length : value.length)),
                value,
                associatedType: 'Wellness'
              } as QuestionAnswerItem;
            });

          const answersPayload: SubmitAnswersRequest = { referralId, answers };

          this.funnelService.submitQuestionAnswers(answersPayload).subscribe({
            next: () => {
              // Submit health metrics separately
              this.trySubmitHealthMetrics(referralId);
              if (!isPhoneVerified) {
                this.startVerificationFlow();
              } else {
                // Clear state before navigating to thank-you for already verified users
                this.clearState();
                this.router.navigate(['/thank-you']);
              }
            },
            error: (ansErr) => {
              console.error('Failed to submit answers', ansErr);
              // Still attempt to submit health metrics
              this.trySubmitHealthMetrics(referralId);
            }
          });
        }
      },
      error: (refErr) => {
        console.error('Failed to create referral', refErr);
      }
    });
  }

  private trySubmitHealthMetrics(referralId: string): void {
    if (this.isBmiSectionValid() && this.goalWeight !== null && this.isGoalWeightValid() && this.heightFeet !== null && this.heightInches !== null && this.weight !== null) {
      const payload: HealthMetricsRequest = {
        referralId,
        heightFeet: this.heightFeet,
        heightInches: this.heightInches,
        weight: this.weight,
        bmi: Number(this.progressValue ? this.progressValue.toFixed(1) : 0),
        idealWeight: this.goalWeight
      };
      this.funnelService.submitHealthMetrics(payload).subscribe({
        next: () => {},
        error: (err) => console.error('Failed to submit health metrics', err)
      });
    }
  }

  private detectMobile(): boolean {
    if (typeof navigator === 'undefined') return false;
    return /Mobi|Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(navigator.userAgent);
  }

  private toIsoDate(mmddyyyy: string): string {
    const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(mmddyyyy);
    if (!m) return '';
    const month = m[1];
    const day = m[2];
    const year = m[3];
    return `${year}-${month}-${day}`;
  }

  private toDateInputValue(mmddyyyy: string): string {
    const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(mmddyyyy);
    if (!m) return '';
    const month = m[1];
    const day = m[2];
    const year = m[3];
    return `${year}-${month}-${day}`;
  }

  private toE164Us(phone: string): string {
    const digits = (phone || '').replace(/\D/g, '');
    if (!digits) return '';
    if (digits.startsWith('1') && digits.length === 11) {
      return `+${digits}`;
    }
    if (digits.length === 10) {
      return `+1${digits}`;
    }
    // Fallback: if already contains +, keep; else prefix +
    if ((phone || '').trim().startsWith('+')) return (phone || '').trim();
    return `+${digits}`;
  }

  private extractBirthSex(): string | null {
    // Prefer detection by options including Male/Female
    const byOptions = this.questions.find((q: any) => q?.type === 'radio' && Array.isArray(q?.options) && q.options.some((o: any) => /^(male|female)$/i.test(String(o))));
    if (byOptions && typeof byOptions.answer === 'string' && /^(male|female)$/i.test(byOptions.answer)) {
      return this.normalizeBirthSex(byOptions.answer);
    }
    // Fallback to text matching
    const byText = this.questions.find((q: any) => {
      const text = `${q?.question_heading || ''} ${q?.question || ''}`.toLowerCase();
      return q?.type === 'radio' && (text.includes('birth sex') || text.includes('sex at birth') || text.includes('gender'));
    });
    if (byText && typeof byText.answer === 'string') {
      return this.normalizeBirthSex(byText.answer);
    }
    return null;
  }

  private normalizeBirthSex(value: string): string {
    const v = (value || '').toString().trim().toLowerCase();
    if (/(^|\b)(male|man|m)(\b|$)/i.test(v)) return 'Male';
    if (/(^|\b)(female|woman|f)(\b|$)/i.test(v)) return 'Female';
    return value;
  }

  private isExcludedFromAnswers(q: any): boolean {
    if (!q) return true;
    if (q.type === 'dob' || q.type === 'bmi' || q.type === 'weight') return true;
    const text = `${q?.question_heading || ''} ${q?.question || ''}`.toLowerCase();
    if (q.type === 'radio' && (text.includes('birth sex') || text.includes('sex at birth') || text.includes('gender'))) {
      return true;
    }
    return false;
  }

  // Address helpers (validation removed per request)

  onStateChange(): void {
    this.state = (this.state || '').toUpperCase();
    this.onFinalStepFieldChange();
  }

  openTermsCondition(){
    this._modalService.show(ConsentModalComponent, {
      class: 'modal-dialog modal-dialog-centered modal-xl',
      ignoreBackdropClick: true
    });
  }

  handleAddressChange = (e: any) => {
    setTimeout(() => {
      e.address_components.forEach(
        (component: { types: any; long_name: any; short_name: any }) => {
          let types = component.types;
          types.forEach((type: string) => {
            if (type === "locality" || type === "sublocality") {
              this.city = component.long_name;
            }
            if (type === "administrative_area_level_1") {
              this.state = component.short_name?.toUpperCase();
            }
            if (type === "postal_code") {
              this.zip = component.long_name;
            }
            if (type === "street_number") {
              this.address = component.short_name;
            }
            if (type === "route") {
              this.address += " " + component.short_name;
            }
          });
        }
      );
      this.onFinalStepFieldChange();
    }, 200);
  };

}
